import React, { useState, useRef } from 'react';
import { Container, Card, Button, Alert, Progress, Icon } from '../../../../core/ui';
import { useNavigate } from 'react-router-dom';
import { uploadStatement } from '../../api/budget';
import { useTranslation } from '../../../../core/context/TranslationContext';
import styles from './UploadStatement.module.css';

const MAX_BYTES = 10 * 1024 * 1024;

const UploadStatement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const parseFailureMessage = (data) => {
    const reason = data?.reason;
    const key = `budget.uploadErrorParse.${reason}`;
    const specific = reason ? t(key) : null;
    const headline = specific && specific !== key ? specific : t('budget.uploadErrorParse');
    return { headline, detail: data?.message || null };
  };

  const acceptFile = (f) => {
    if (!f) return;
    if (f.type !== 'application/pdf') {
      setError(t('budget.uploadErrorPdfOnly'));
      return;
    }
    if (f.size > MAX_BYTES) {
      setError(t('budget.uploadErrorTooLarge'));
      return;
    }
    setFile(f);
    setError(null);
    setSuccess(null);
  };

  const onFileChange = (e) => acceptFile(e.target.files[0]);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!loading) setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (!loading) acceptFile(e.dataTransfer.files[0]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setLoading(true);
    setError(null);
    setProgress(0);
    try {
      const res = await uploadStatement(file, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
      });
      if (res.status === 409) {
        setError(t('budget.uploadErrorDuplicate'));
        return;
      }
      if (res.status === 422) {
        setError(parseFailureMessage(res.data));
        return;
      }
      if (res.status >= 400) {
        setError(t('budget.uploadError'));
        return;
      }
      setSuccess(res.data);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      setError(err?.message || t('budget.uploadError'));
    } finally {
      setLoading(false);
    }
  };

  const dropzoneClass = [
    styles.dropzone,
    dragging ? styles.dropzoneActive : '',
    file ? styles.dropzoneHasFile : '',
    loading ? styles.dropzoneDisabled : '',
  ].filter(Boolean).join(' ');

  return (
    <Container className={styles.containerNarrow}>
      <Card>
        <Card.Header>
          <h5 className={styles.title}>{t('budget.uploadStatement')}</h5>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onDismiss={() => setError(null)}>
              {typeof error === 'string' ? error : (
                <>
                  <div>{error.headline}</div>
                  {error.detail && (
                    <div className={styles.errorDetail}>
                      {t('budget.uploadErrorDetail')}: <code>{error.detail}</code>
                    </div>
                  )}
                </>
              )}
            </Alert>
          )}
          {success && (
            <Alert variant="success" dismissible onDismiss={() => setSuccess(null)}>
              <div>
                {t('budget.uploadSuccess', {
                  count: success.transactionCount,
                  bank: success.detectedBank,
                })}
              </div>
              <div className={styles.mt2}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/budget/statements/${success.statementId}`)}
                >
                  {t('budget.viewStatement')}
                </Button>
              </div>
            </Alert>
          )}

          <form onSubmit={onSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.label}>{t('budget.selectPdf')}</label>

              <div
                className={dropzoneClass}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept="application/pdf"
                  onChange={onFileChange}
                  disabled={loading}
                  className={styles.fileInputHidden}
                  tabIndex={-1}
                />

                <div className={styles.dropzoneIcon}>
                  <Icon name={file ? 'fa-file-pdf' : 'fa-cloud-upload-alt'} />
                </div>

                {file ? (
                  <span className={styles.dropzoneFileName}>{file.name}</span>
                ) : (
                  <p className={styles.dropzoneText}>
                    <strong>Choose a PDF</strong> or drag it here
                  </p>
                )}
              </div>

              <p className={styles.hint}>{t('budget.pdfHint')}</p>
            </div>

            {loading && progress > 0 && (
              <div className={styles.progressWrapper}>
                <Progress value={progress} label={`${progress}%`} />
                {progress === 100 && (
                  <p className={styles.processingNote}>Parsing statement…</p>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={!file || loading}
              className={styles.submitBtn}
            >
              {loading ? (
                <>
                  <span className={styles.spinner} />
                  {t('budget.processing')}
                </>
              ) : (
                t('budget.uploadStatement')
              )}
            </Button>
          </form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default UploadStatement;
